import styled from "styled-components";

import { AttributesView } from "./attributes/AttributesView";
import HeaderDetail from "./HeaderDetail";
import { Inspector } from "./Inspector";

export type TokenUsage = {
  service_alias_name: string;
  input_tokens?: number | null;
  cached_input_tokens?: number | null;
  output_tokens?: number | null;
};

type TokenKind = "input_tokens" | "cached_input_tokens" | "output_tokens";

const KIND_LABELS: Record<TokenKind, string> = {
  input_tokens: "Input tokens",
  cached_input_tokens: "Cached input tokens",
  output_tokens: "Output tokens",
};

const formatTokens = (count: number) => Intl.NumberFormat().format(count);

// Focusable so the popover also opens from the keyboard; the inner span keeps
// Inspector's dashed-underline affordance.
const TotalButton = styled.button`
  background: none;
  border: 0;
  padding: 0;
  font: inherit;
  color: inherit;
`;

const TokenBreakdown = ({
  usage,
  kind,
}: {
  usage: Array<TokenUsage>;
  kind: TokenKind;
}) => (
  <>
    <h6>{KIND_LABELS[kind]}</h6>
    <AttributesView
      attributes={usage.map((u) => ({
        name: u.service_alias_name,
        label: u.service_alias_name,
        value: formatTokens(u[kind] ?? 0),
        showMonoLabel: true,
        showMonoValue: false,
      }))}
    />
  </>
);

const TokenTotalField = ({
  usage,
  kind,
}: {
  usage: Array<TokenUsage>;
  kind: TokenKind;
}) => {
  const total = usage.reduce((sum, u) => sum + (u[kind] ?? 0), 0);
  return (
    <HeaderDetail.Field label={KIND_LABELS[kind]}>
      <Inspector
        showLoading={false}
        View={TokenBreakdown}
        viewProps={{ usage, kind }}
      >
        <TotalButton
          type="button"
          aria-label={`${KIND_LABELS[kind]}: ${formatTokens(total)}. Show breakdown by service alias.`}
        >
          <span>{formatTokens(total)}</span>
        </TotalButton>
      </Inspector>
    </HeaderDetail.Field>
  );
};

/**
 * Input, cached input and output token totals for a session, with a
 * per-service-alias breakdown on hover. Cached input tokens are a subset of
 * input tokens. Renders HeaderDetail fields, so place it inside a
 * HeaderDetail.
 */
export const TokenUsageFields = ({ usage }: { usage: Array<TokenUsage> }) => (
  <>
    <TokenTotalField usage={usage} kind="input_tokens" />
    <TokenTotalField usage={usage} kind="cached_input_tokens" />
    <TokenTotalField usage={usage} kind="output_tokens" />
  </>
);
